import { CHARACTER_INDEX } from './character_index.generated.js'
import { blockForCodepoint, allCuratedCodepoints } from './unicode_blocks.js'

export const SIMILARITY_BUCKETS = [
  { id: 'same-block', label: 'Same block' },
  { id: 'adjacent', label: 'Next door' },
  { id: 'same-shape', label: 'Same shape' },
  { id: 'same-use-case', label: 'Same use case' },
  { id: 'weird', label: 'Weird neighbors' },
]

const CURATED_SET = new Set(allCuratedCodepoints())

let unicodeNames = null
let unicodeNamesPromise = null

export function ensureUnicodeNames() {
  if (unicodeNames) return Promise.resolve(unicodeNames)
  if (!unicodeNamesPromise) {
    unicodeNamesPromise = import('./unicode_names.generated.js').then((module) => {
      unicodeNames = module.UNICODE_NAMES
      return unicodeNames
    })
  }
  return unicodeNamesPromise
}

function unicodeNameFor(codepoint) {
  return (
    unicodeNames?.[codepoint] ||
    `U+${codepoint.toString(16).toUpperCase().padStart(4, '0')}`
  )
}

export function getCharacterMeta(codepoint) {
  const meta = CHARACTER_INDEX[codepoint]
  if (meta) return meta

  return {
    name: unicodeNameFor(codepoint),
    tags: [],
    shapes: [],
    useCases: [],
    blockId: blockForCodepoint(codepoint),
  }
}

export function getSimilar(codepoint, bucketId) {
  const meta = getCharacterMeta(codepoint)
  const exclude = new Set([codepoint])

  switch (bucketId) {
    case 'same-block':
      return sameBlock(codepoint, meta, exclude)
    case 'adjacent':
      return adjacent(codepoint, meta, exclude)
    case 'same-shape':
      return sameShape(codepoint, meta, exclude)
    case 'same-use-case':
      return sameUseCase(codepoint, meta, exclude)
    case 'weird':
      return weirdNeighbors(codepoint, meta, exclude)
    default:
      return []
  }
}

export function getAllSimilarBuckets(codepoint) {
  return SIMILARITY_BUCKETS.map((bucket) => ({
    ...bucket,
    codepoints: getSimilar(codepoint, bucket.id),
  })).filter((bucket) => bucket.codepoints.length > 0)
}

/** Merged, deduped related codepoints (same-block + adjacent first, then other buckets). */
export function getRelatedCodepoints(codepoint) {
  const seen = new Set([codepoint])
  const result = []

  for (const bucket of SIMILARITY_BUCKETS) {
    for (const cp of getSimilar(codepoint, bucket.id)) {
      if (seen.has(cp)) continue
      seen.add(cp)
      result.push(cp)
    }
  }

  return result
}

function sameBlock(codepoint, meta, exclude) {
  if (!meta.blockId) return []

  return allCuratedCodepoints()
    .filter((cp) => CHARACTER_INDEX[cp]?.blockId === meta.blockId && !exclude.has(cp))
    .sort((a, b) => Math.abs(a - codepoint) - Math.abs(b - codepoint))
    .slice(0, 12)
}

function adjacent(codepoint, meta, exclude) {
  if (!meta.blockId) return []

  const neighbors = []
  for (let offset = 1; offset <= 4; offset++) {
    for (const delta of [offset, -offset]) {
      const cp = codepoint + delta
      if (exclude.has(cp) || !CURATED_SET.has(cp)) continue
      if (CHARACTER_INDEX[cp]?.blockId !== meta.blockId) continue
      neighbors.push(cp)
    }
  }

  return neighbors.slice(0, 12)
}

function sameShape(codepoint, meta, exclude) {
  if (!meta.shapes?.length) return []

  const shapeSet = new Set(meta.shapes)
  return allCuratedCodepoints()
    .filter((cp) => {
      if (exclude.has(cp)) return false
      const shapes = CHARACTER_INDEX[cp]?.shapes || []
      return shapes.some((shape) => shapeSet.has(shape))
    })
    .sort((a, b) => overlapScore(meta, CHARACTER_INDEX[b]) - overlapScore(meta, CHARACTER_INDEX[a]))
    .slice(0, 12)
}

function sameUseCase(codepoint, meta, exclude) {
  if (!meta.useCases?.length) return []

  const useCaseSet = new Set(meta.useCases)
  return allCuratedCodepoints()
    .filter((cp) => {
      if (exclude.has(cp)) return false
      const useCases = CHARACTER_INDEX[cp]?.useCases || []
      return useCases.some((useCase) => useCaseSet.has(useCase))
    })
    .sort((a, b) => Math.abs(a - codepoint) - Math.abs(b - codepoint))
    .slice(0, 12)
}

function weirdNeighbors(codepoint, meta, exclude) {
  const tagSet = new Set(meta.tags || [])
  const shapeSet = new Set(meta.shapes || [])

  return allCuratedCodepoints()
    .filter((cp) => {
      if (exclude.has(cp)) return false
      const other = CHARACTER_INDEX[cp]
      if (!other) return false
      if (other.blockId === meta.blockId) return false

      const sharedShape = (other.shapes || []).some((shape) => shapeSet.has(shape))
      const sharedTag = (other.tags || []).some((tag) => tagSet.has(tag))
      return sharedShape || sharedTag
    })
    .sort((a, b) => overlapScore(meta, CHARACTER_INDEX[b]) - overlapScore(meta, CHARACTER_INDEX[a]))
    .slice(0, 12)
}

function overlapScore(meta, other) {
  if (!other) return 0
  const tagOverlap = (other.tags || []).filter((tag) => (meta.tags || []).includes(tag)).length
  const shapeOverlap = (other.shapes || []).filter((shape) => (meta.shapes || []).includes(shape)).length
  return tagOverlap + shapeOverlap * 2
}

export function isCuratedCodepoint(codepoint) {
  return CURATED_SET.has(codepoint)
}

export function codepointMatchesSearch(codepoint, queryLower) {
  if (!queryLower) return true

  const meta = CHARACTER_INDEX[codepoint]
  if (meta) {
    if (meta.name.toLowerCase().includes(queryLower)) return true
    if ((meta.tags || []).some((tag) => tag.toLowerCase().includes(queryLower))) return true
    if ((meta.shapes || []).some((shape) => shape.toLowerCase().includes(queryLower))) return true
    if ((meta.useCases || []).some((useCase) => useCase.toLowerCase().includes(queryLower))) return true
    return false
  }

  const name = unicodeNames?.[codepoint]
  if (!name) return false

  return name.toLowerCase().includes(queryLower)
}

export function categoryNeedsUnicodeNamesForSearch(codepoints) {
  return codepoints.some((cp) => !CHARACTER_INDEX[cp])
}
